<template>
  <div
    :class="[
      WIDGET_CONSTANTS.CSS_CLASSES.CHATBOT,
      WIDGET_CONSTANTS.CSS_CLASSES.WIDGET,
      { 'siddchatbot-widget--fill-parent': shouldFillParent },
    ]"
    :style="shouldFillParent ? { height: '100%' } : {}"
    :data-position="mappedPosition"
    :data-chatbot-id="props.chatbotId || props.tenantId"
  >
    <div
      v-if="!chatbot.store.isInitialized && !chatbot.store.error"
      class="sidd-loading-container"
      :class="[
        shouldFillParent ? 'sidd-loading-container--fill-parent' : 'fixed',
        !shouldFillParent
          ? getLoadingPositionClasses(chatbot.store.configuration?.position)
          : '',
      ]"
    >
      <div class="sidd-loading-spinner">
        <Spinner
          size="lg"
          :color="chatbot.store.brand?.brandColor || undefined"
          aria-label="Loading chatbot..."
        />
      </div>
    </div>

    <ChatTrigger
      v-if="!isOpen && !shouldFillParent"
      :is-open="isOpen"
      :is-hidden="false"
      :has-unread-messages="hasUnreadMessages"
      :unread-count="unreadCount"
      @click="handleTriggerClick"
    />

    <div
      v-if="
        chatbot.store.isInitialized && !isActive && (isOpen || shouldFillParent)
      "
      class="sidd-chat-window sidd-chat-window--inactive flex flex-col bg-background text-foreground pointer-events-auto"
      :class="{
        'sidd-chat-window--open': isOpen,
        'sidd-chat-window--fill-parent': shouldFillParent,
      }"
      :data-position="mappedPosition"
      role="status"
      aria-live="polite"
    >
      <div class="sidd-chat-container flex flex-col flex-1 min-h-0">
        <div v-if="!shouldFillParent" class="flex justify-end p-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close"
            @click="handleClose"
          >
            <X :size="20" />
          </Button>
        </div>
        <StatusMessage
          title="Chat Paused"
          description="This chat is temporarily paused by the administrator. Please check back later."
        />
      </div>
    </div>

    <ChatWindow
      v-show="(isOpen || shouldFillParent) && isActive"
      :is-open="isOpen || shouldFillParent"
      :user-id="userId"
      :session-id="chatSession.store.activeSessionId || undefined"
      :initial-message="initialMessage"
      @close="handleClose"
      @minimize="handleMinimize"
      @menu="handleMenu"
      @message-sent="handleMessageSent"
      @session-started="handleSessionStarted"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watchEffect, computed, watch } from 'vue'
import { useChatbot } from '@/composables/useChatbot'
import { useTheme } from '@/composables/useTheme'
import { useAuth } from '@/composables/useAuth'
import { useChatSession } from '@/composables/useChatSession'
import { provideChatbotContext } from '@/composables/useChatbotContext'
import { useAnalytics } from '@/composables/useAnalytics'
import { logger } from '@/utils/logger'
import ChatTrigger from '@/components/ChatTrigger/ChatTrigger.vue'
import ChatWindow from '@/components/ChatWindow/ChatWindow.vue'
import { Spinner } from '@/components/Spinner'
import { StatusMessage } from '@/components/StatusMessage'
import { Button } from '@/lib/ui/button'
import { X } from 'lucide-vue-next'
import type { IChatMessage } from '@/types'
import { DeploymentType, Position } from '@/types/chatbot-types'
import { WIDGET_CONSTANTS } from '@/types/constants'
import { ANALYTICS_EVENTS } from '@/types/analytics-types'

import type { ISiddChatBotProps, ISiddChatBotEmits } from './types'

const props = defineProps<ISiddChatBotProps>()
const emit = defineEmits<ISiddChatBotEmits>()

if (!props.chatbotId && !props.tenantId) {
  throw new Error('Either chatbotId or tenantId must be provided')
}

// Provide chatbot context for child composables via Vue's provide/inject
// This ensures proper isolation when multiple chatbot instances exist
const chatbotContext = provideChatbotContext(
  props.chatbotId,
  props.tenantId,
  props.userId
)

const deploymentType =
  props.siddDuoConfig && Object.keys(props.siddDuoConfig).length > 0
    ? DeploymentType._SiddDuo
    : undefined

const chatbot = useChatbot({
  chatbotId: props.chatbotId,
  tenantId: props.tenantId,
  deploymentType,
})

if (props.siddDuoConfig) {
  chatbot.setSiddDuoConfig(props.siddDuoConfig)
}
const theme = useTheme()
const auth = useAuth()
const chatSession = useChatSession({
  persistMessages: true,
  autoScrollToBottom: true,
})

const analytics = useAnalytics()

if (props.analyticsCallback) {
  analytics.setAnalyticsCallback(props.analyticsCallback)
}

analytics.setChatbotContext(props.chatbotId, props.tenantId)

const isOpen = ref(false)
const unreadCount = ref(0)
const hasUnreadMessages = ref(false)

const shouldFillParent = computed(
  () =>
    chatbot.store.configuration?.position === Position._FillParent ||
    props.overrideConfiguration?.position === Position._FillParent
)

const isActive = computed(() => chatbot.store.configuration?.isActive ?? false)

const mappedPosition = computed(() => {
  const position = chatbot.store.configuration?.position
  if (!position) return WIDGET_CONSTANTS.DEFAULT_POSITION
  return (
    WIDGET_CONSTANTS.POSITION_MAPPING[
      position as keyof typeof WIDGET_CONSTANTS.POSITION_MAPPING
    ] || WIDGET_CONSTANTS.DEFAULT_POSITION
  )
})

const getLoadingPositionClasses = (position?: Position): string => {
  switch (position) {
    case Position._BottomLeft:
      return 'bottom-5 left-5'
    case Position._TopRight:
      return 'top-5 right-5'
    case Position._TopLeft:
      return 'top-5 left-5'
    case Position._FillParent:
      return '' // Handled by sidd-loading-container--fill-parent class
    case Position._BottomRight:
    default:
      return 'bottom-5 right-5'
  }
}

const toggleChat = (): void => {
  if (isOpen.value) {
    handleClose()
  } else {
    handleOpen()
  }
}

const handleOpen = (): void => {
  isOpen.value = true
  hasUnreadMessages.value = false
  unreadCount.value = 0

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.CHATBOT_OPENED,
    timestamp: new Date().toISOString(),
    payload: {
      position: chatbot.store.configuration?.position,
      deploymentType: deploymentType || 'PublicWidget',
    },
    source: 'chatbot',
    sessionId: chatSession.store.activeSessionId || undefined,
    userId: props.userId,
    chatbotId: props.chatbotId,
    tenant: props.tenantId,
  })

  emit('open')
}

const handleClose = (): void => {
  isOpen.value = false

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.CHATBOT_CLOSED,
    timestamp: new Date().toISOString(),
    source: 'chatbot',
    sessionId: chatSession.store.activeSessionId || undefined,
    userId: props.userId,
    chatbotId: props.chatbotId,
    tenant: props.tenantId,
  })

  emit('close')
}

const handleMenu = (): void => {
  emit('menu')
}

const handleMinimize = (): void => {
  isOpen.value = false

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.CHATBOT_MINIMIZED,
    timestamp: new Date().toISOString(),
    source: 'chatbot',
    sessionId: chatSession.store.activeSessionId || undefined,
    userId: props.userId,
    chatbotId: props.chatbotId,
    tenant: props.tenantId,
  })
}

const handleTriggerClick = (): void => {
  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.TRIGGER_CLICKED,
    timestamp: new Date().toISOString(),
    source: 'chatbot',
    sessionId: chatSession.store.activeSessionId || undefined,
    userId: props.userId,
    chatbotId: props.chatbotId,
    tenant: props.tenantId,
  })

  toggleChat()

  if (props.initialMessage && !chatSession.store.hasMessages) {
    chatSession.sendMessage(props.initialMessage)
  }
}

const handleMessageSent = (message: IChatMessage): void => {
  if (!isOpen.value) {
    hasUnreadMessages.value = true
    unreadCount.value += 1
  }

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.MESSAGE_SENT,
    timestamp: new Date().toISOString(),
    payload: {
      characterCount: message.userMessage?.length || 0,
    },
    source: 'chatbot',
    sessionId: chatSession.store.activeSessionId || undefined,
    userId: props.userId,
    chatbotId: props.chatbotId,
    tenant: props.tenantId,
  })

  emit('message-sent', message)
}

const handleSessionStarted = (sessionId: string): void => {
  analytics.setUserContext(props.userId, sessionId)

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.SESSION_STARTED,
    timestamp: new Date().toISOString(),
    source: 'chatbot',
    sessionId: sessionId,
    userId: props.userId,
    chatbotId: props.chatbotId,
    tenant: props.tenantId,
  })

  emit('session-started', sessionId)
}

const initializeChat = async (): Promise<void> => {
  try {
    const isSiddDuo =
      !!props.siddDuoConfig && Object.keys(props.siddDuoConfig).length > 0

    const scopedClient = chatbotContext.apiClient

    if (isSiddDuo) {
      if (props.getAuthInfo) {
        scopedClient.setAuthCallback(async () => {
          const authInfo = await props.getAuthInfo!()

          return {
            token: authInfo.accessToken,
            tenantId: authInfo.userTenant,
            userId: authInfo.userId || props.userId,
          }
        })
        logger.info('SiddDuo auth callback configured', {
          component: 'SiddChatBot',
        })
      } else {
        logger.warn(
          'SiddDuo config provided but getAuthInfo callback is missing',
          { component: 'SiddChatBot' }
        )
      }
    } else if (props.getAuthInfo) {
      scopedClient.setAuthCallback(async () => {
        const authInfo = await props.getAuthInfo!()
        return {
          token: authInfo.accessToken,
          tenantId: authInfo.userTenant,
          userId: authInfo.userId || props.userId,
        }
      })
      logger.info('WebDirect auth callback configured', {
        component: 'SiddChatBot',
      })
    } else {
      scopedClient.setAuthCallback(null)
      auth.clearAuth()
      logger.info('PublicWidget mode - no auth configured', {
        component: 'SiddChatBot',
      })
    }

    await chatbot.loadChatbot()

    if (props.overrideConfiguration || props.isDemoMode) {
      chatbot.applyOverrideConfiguration({
        position: props.overrideConfiguration?.position,
        autoOpen: props.overrideConfiguration?.autoOpen,
        // In demo/preview mode, force the chatbot to behave as active so it can
        // be rendered and interacted with even when its backend config is inactive.
        isActive: props.isDemoMode ? true : undefined,
      })
    }

    theme.initializeTheme()

    if (props.styleConfig) {
      theme.initializeStyles(props.styleConfig)
    }

    if (
      chatbot.store.configuration?.autoOpen &&
      chatbot.store.configuration?.isActive
    ) {
      setTimeout(() => handleOpen(), 500)
    }

    analytics.trackEvent({
      eventName: ANALYTICS_EVENTS.CHATBOT_READY,
      timestamp: new Date().toISOString(),
      payload: {
        deploymentType: deploymentType || 'PublicWidget',
        position: chatbot.store.configuration?.position,
        autoOpen: chatbot.store.configuration?.autoOpen,
      },
      source: 'chatbot',
      sessionId: chatSession.store.activeSessionId || undefined,
      userId: props.userId,
      chatbotId: props.chatbotId,
      tenant: props.tenantId,
    })

    emit('ready')
  } catch (error: any) {
    logger.error(
      'Failed to initialize chat',
      error,
      { component: 'SiddChatBot' },
      error
    )

    emit('error', error)
  }
}

watchEffect(() => {
  if (!isOpen.value && chatSession.store.hasMessages) {
    const lastMessage = chatSession.store.lastMessage
    if (lastMessage && !(lastMessage.userMessage && !lastMessage.answer)) {
      hasUnreadMessages.value = true
      unreadCount.value += 1
    }
  }
})

watch(
  () => props.styleConfig?.zoomFactor,
  (newZoom, oldZoom) => {
    if (newZoom !== undefined && newZoom !== oldZoom) {
      theme.applyZoomFactor(newZoom)
    }
  }
)

onMounted(() => {
  if (props.chatbotId || props.tenantId) {
    initializeChat()
  }
})

onUnmounted(() => {
  chatSession.cleanup()
  theme.resetTheme()
})

defineExpose({
  open: handleOpen,
  close: handleClose,
  toggle: toggleChat,
  isOpen: () => isOpen.value,
  sessionId: () => chatSession.store.activeSessionId,
  sendMessage: (message: string) => chatSession.sendMessage(message),
  clearMessages: () => chatSession.clearMessages(),
  clearHistory: () => chatSession.store.clearHistory(),
  endSession: () => chatSession.endSession(),
  auth,
})
</script>

<style>
.siddchatbot-widget {
  position: fixed;
  pointer-events: none;
  z-index: var(--widget-z-index);
}

.siddchatbot-widget--fill-parent {
  position: relative !important;
  width: 100%;
  height: 100% !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.siddchatbot-widget.siddchatbot-widget--fill-parent {
  height: 100% !important;
}

.siddchatbot-widget > * {
  pointer-events: auto;
}

.sidd-loading-container {
  pointer-events: none;
  z-index: var(--widget-trigger-z-index);
}

.sidd-loading-container--fill-parent {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.sidd-loading-spinner {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: hsl(var(--background));
  border: 1px solid hsl(var(--border));
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 4px 6px -1px hsl(var(--foreground) / 0.1),
    0 2px 4px -2px hsl(var(--foreground) / 0.1);
  pointer-events: auto;
}

.siddchatbot-widget[data-position='bottom-right'] {
  bottom: var(--widget-offset);
  right: var(--widget-offset);
}

.siddchatbot-widget[data-position='bottom-left'] {
  bottom: var(--widget-offset);
  left: var(--widget-offset);
}

.siddchatbot-widget[data-position='top-right'] {
  top: var(--widget-offset);
  right: var(--widget-offset);
}

.siddchatbot-widget[data-position='top-left'] {
  top: var(--widget-offset);
  left: var(--widget-offset);
}

.siddchatbot-widget[data-position='fill-parent'] {
  position: relative;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.siddchatbot-widget[data-position='bottom-right'] .sidd-chat-window,
.siddchatbot-widget[data-position='bottom-right'] .sidd-chat-trigger {
  bottom: var(--widget-offset);
  right: var(--widget-offset);
}

.siddchatbot-widget[data-position='bottom-left'] .sidd-chat-window,
.siddchatbot-widget[data-position='bottom-left'] .sidd-chat-trigger {
  bottom: var(--widget-offset);
  left: var(--widget-offset);
}

.siddchatbot-widget[data-position='top-right'] .sidd-chat-window,
.siddchatbot-widget[data-position='top-right'] .sidd-chat-trigger {
  top: var(--widget-offset);
  right: var(--widget-offset);
}

.siddchatbot-widget[data-position='top-left'] .sidd-chat-window,
.siddchatbot-widget[data-position='top-left'] .sidd-chat-trigger {
  top: var(--widget-offset);
  left: var(--widget-offset);
}

.siddchatbot-widget * {
  box-sizing: border-box;
}
</style>
